import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, CardBody } from 'reactstrap';
import { SkeletonTable } from '../../../../../Components/Common/Skeleton';
import { EmptyState } from '../../../../../Components/Common/ListStates';
import ActionButton from '../../../../../Components/Common/ActionButton';
import TenderNotClosedAlert from '../EvaluationOperational/Components/TenderNotClosedAlert';
import useSubmittedBidder from '../../../hooks/useSubmittedBidder';
import useNegotiationPackage from '../../../hooks/useNegotiationPackage';
import useSaveNegotiationSelection from '../../../hooks/useSaveNegotiationSelection';
import {
  buildNegotiationHandoffDraft,
  canAccessEvaluationOperationalData,
  getEvaluationOperationalLockReason,
  isEvaluationSaved,
} from '../../../utils/tenderEvaluationHelpers';
import notify from '../../../../../utils/notify';
import { confirmAction } from '../../../../../utils/confirm';
import NegotiationWizard from './NegotiationWizard';
import NegotiationOfferTable from './NegotiationOfferTable';
import {
  buildNegotiationSaveSummary,
  buildSelectionFromPackage,
  formatNegotiationValidationMessage,
  getParcelId,
  validateNegotiationSelection,
} from './negotiationWizardUtils';
import { hasNegotiationOfferId } from '../../../../../shared/offer-domain/negotiation';

const offersEqual = (a = [], b = []) =>
  a.length === b.length &&
  a.every(
    (entry, index) =>
      entry?.rowKey === b[index]?.rowKey &&
      entry?.selectionKey === b[index]?.selectionKey &&
      entry?.idOffer === b[index]?.idOffer &&
      entry?.kind === b[index]?.kind &&
      entry?.quantityIncotermId === b[index]?.quantityIncotermId &&
      entry?.quantityIncotermCounterId === b[index]?.quantityIncotermCounterId
  );

const Negotiation = ({
  procurement,
  procId,
  parcels = [],
  isLoadingParcels = false,
  parcelsError = null,
  onRetryParcels,
  isLoadingProcurement = false,
  isActive = true,
}) => {
  const navigate = useNavigate();
  const [selectionByParcel, setSelectionByParcel] = useState({});
  const [prefillByParcel, setPrefillByParcel] = useState({});
  const [blockedRequirements, setBlockedRequirements] = useState({});
  const [clearTokenByParcel, setClearTokenByParcel] = useState({});
  const ignoreEmptyUntilRef = useRef({});
  const prefillAppliedRef = useRef(false);
  const { saveSelection, isLoading: isSaving } = useSaveNegotiationSelection();

  const lockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessData = canAccessEvaluationOperationalData(procurement);
  const isSaved = isEvaluationSaved(procurement);
  const procurementId = procurement?.id || procId;

  const { isLoading: isLoadingBidders } = useSubmittedBidder(procurementId, {
    enabled: Boolean(procurementId && canAccessData && isSaved),
    isActive: Boolean(isActive && canAccessData && isSaved),
  });

  const { data: savedPackage, isLoading: isLoadingPackage } =
    useNegotiationPackage(procurementId, {
      enabled: Boolean(procurementId && canAccessData && isSaved),
    });

  const isDataReady =
    !isLoadingBidders && !isLoadingParcels && !isLoadingPackage;

  // No placeholder DU: an id like 'default' must never reach the payload.
  const parcelItems = parcels;

  // Prefill once from the saved package, after parcels are known.
  useEffect(() => {
    if (prefillAppliedRef.current || !isDataReady || !parcels.length) return;
    prefillAppliedRef.current = true;
    if (!savedPackage) return;
    const prefill = buildSelectionFromPackage(
      savedPackage,
      parcels,
      procurement
    );
    setPrefillByParcel(prefill);
    setSelectionByParcel(prefill);
  }, [isDataReady, parcels, savedPackage, procurement]);

  const selectedOffers = useMemo(
    () =>
      Object.values(selectionByParcel)
        .flatMap(entry => entry?.offers ?? [])
        .filter(offer => hasNegotiationOfferId(offer?.idOffer)),
    [selectionByParcel]
  );

  const selectionValidation = useMemo(
    () => validateNegotiationSelection(parcelItems, selectionByParcel),
    [parcelItems, selectionByParcel]
  );

  // Semua Delivery Unit sudah dimulai: tidak ada pilihan baru untuk disimpan.
  const allParcelsLocked = useMemo(
    () =>
      parcelItems.length > 0 &&
      parcelItems.every(parcel =>
        Boolean(selectionByParcel[String(getParcelId(parcel))]?.locked)
      ),
    [parcelItems, selectionByParcel]
  );

  const armIgnoreEmpty = parcelKey => {
    ignoreEmptyUntilRef.current[parcelKey] = Date.now() + 800;
  };

  const handleOfferAvailability = useCallback(
    ({ parcelId, requirementIndex, hasSelectableOffer }) => {
      if (parcelId == null || requirementIndex == null) return;
      const blockKey = `${parcelId}:${requirementIndex}`;
      if (hasSelectableOffer) {
        setBlockedRequirements(prev => {
          if (!prev[blockKey]) return prev;
          const next = { ...prev };
          delete next[blockKey];
          return next;
        });
        return;
      }
      setBlockedRequirements(prev =>
        prev[blockKey] ? prev : { ...prev, [blockKey]: true }
      );
      setSelectionByParcel(prev => {
        const parcelKey = String(parcelId);
        if (
          prev[parcelKey]?.locked ||
          prev[parcelKey]?.requirementIndex !== requirementIndex
        ) {
          return prev;
        }
        const next = { ...prev };
        delete next[parcelKey];
        return next;
      });
    },
    []
  );

  const handleSelectRequirement = (parcelId, requirementIndex) => {
    if (blockedRequirements[`${parcelId}:${requirementIndex}`]) return;
    const parcelKey = String(parcelId);
    const prevEntry = selectionByParcel[parcelKey];
    if (prevEntry?.locked) return;
    const requirementChanged = prevEntry?.requirementIndex !== requirementIndex;

    setSelectionByParcel(prev => {
      if (
        prev[parcelKey]?.requirementIndex === requirementIndex &&
        !requirementChanged
      ) {
        return prev;
      }
      if (!requirementChanged && prev[parcelKey]) return prev;
      return {
        ...prev,
        [parcelKey]: {
          requirementIndex,
          offers: requirementChanged ? [] : (prev[parcelKey]?.offers ?? []),
        },
      };
    });
    if (requirementChanged) {
      armIgnoreEmpty(parcelKey);
      setClearTokenByParcel(prev => ({
        ...prev,
        [parcelKey]: (prev[parcelKey] || 0) + 1,
      }));
    }
  };

  const handleOffersChange = useCallback(
    (parcelId, requirementIndex, offers) => {
      if (parcelId == null || requirementIndex == null) return;
      const parcelKey = String(parcelId);
      const nextOffers = Array.isArray(offers) ? offers : [];
      const ignoreUntil = ignoreEmptyUntilRef.current[parcelKey] || 0;
      if (!nextOffers.length && Date.now() < ignoreUntil) return;

      setSelectionByParcel(prev => {
        const prevEntry = prev[parcelKey];
        if (!prevEntry || prevEntry.locked) return prev;
        const prevOffers = prevEntry?.offers ?? [];

        if (
          !nextOffers.length &&
          prevOffers.length > 0 &&
          prevEntry?.requirementIndex === requirementIndex
        ) {
          return prev;
        }

        if (
          prevEntry?.requirementIndex === requirementIndex &&
          offersEqual(prevOffers, nextOffers)
        ) {
          return prev;
        }

        return {
          ...prev,
          [parcelKey]: {
            requirementIndex,
            offers: nextOffers,
          },
        };
      });
    },
    []
  );

  const handleSaveDraft = useCallback(async () => {
    if (!procurement?.id || !parcelItems.length) return;

    const validation = validateNegotiationSelection(
      parcelItems,
      selectionByParcel
    );
    if (!validation.isComplete) {
      notify.error(formatNegotiationValidationMessage(validation));
      return;
    }

    const confirmed = await confirmAction({
      tone: 'primary',
      title: 'Konfirmasi Penyimpanan Pilihan Negotiation',
      message: `Mohon konfirmasi ringkasan pilihan Negotiation berikut sebelum disimpan:\n\n${buildNegotiationSaveSummary(
        parcelItems,
        selectionByParcel,
        procurement
      )}`,
      confirmLabel: 'Simpan Pilihan',
      cancelLabel: 'Batal',
    });
    if (!confirmed) return;

    await saveSelection({
      draft: buildNegotiationHandoffDraft({
        procurementId: procurement.id,
        selections: selectedOffers,
      }),
      onSuccess: () => navigate(`/apps-negotiation/dtl/${procurement.id}`),
    });
  }, [
    procurement,
    parcelItems,
    selectionByParcel,
    selectedOffers,
    saveSelection,
    navigate,
  ]);

  const renderOfferTable = useCallback(
    ({ parcelId, requirementIndex }) => {
      if (parcelId == null || requirementIndex == null) return null;
      const parcelKey = String(parcelId);
      const prefill = prefillByParcel[parcelKey];
      return (
        <NegotiationOfferTable
          key={`${parcelKey}-req-${requirementIndex}`}
          parcelId={parcelId}
          requirementIndex={requirementIndex}
          onOffersChange={handleOffersChange}
          procurementId={procurementId}
          procurement={procurement}
          parcels={parcels}
          isLoadingParcels={isLoadingParcels}
          selectionClearToken={clearTokenByParcel[parcelKey] || 0}
          isActive={isActive}
          onNegotiationOfferAvailability={handleOfferAvailability}
          preselectedOffers={
            prefill?.requirementIndex === requirementIndex
              ? prefill.offers
              : null
          }
        />
      );
    },
    [
      prefillByParcel,
      handleOffersChange,
      procurementId,
      procurement,
      parcels,
      isLoadingParcels,
      clearTokenByParcel,
      isActive,
      handleOfferAvailability,
    ]
  );

  if (isLoadingProcurement && !procurement) {
    return (
      <Card className='border shadow-md mb-4'>
        <CardBody>
          <SkeletonTable rows={5} columns={6} />
        </CardBody>
      </Card>
    );
  }

  if (!canAccessData) {
    return (
      <TenderNotClosedAlert featureLabel='Negotiation' reason={lockReason} />
    );
  }

  if (!isSaved) {
    return (
      <Alert color='warning' className='mb-0'>
        <h6 className='alert-heading mb-1 fw-semibold'>
          Pilihan Negotiation belum tersedia
        </h6>
        <p className='mb-0 fs-13'>
          Selesaikan dan kunci evaluasi terlebih dahulu. Setelah itu, pilih
          Requirement dan Offer untuk setiap Delivery Unit.
        </p>
      </Alert>
    );
  }

  const actionHint = !selectionValidation.isComplete
    ? formatNegotiationValidationMessage(selectionValidation)
    : null;

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <div className='mb-4'>
          <h5 className='fw-bold text-dark mb-1'>Negotiation Selection</h5>
          <p className='text-muted small mb-0'>
            Lengkapi semua Delivery Unit. Pada setiap Delivery Unit, pilih satu
            Requirement lalu pilih minimal satu Offer. Pilihan tetap tersimpan
            saat berpindah antar Delivery Unit.
          </p>
        </div>

        {!isDataReady ? (
          <SkeletonTable rows={5} columns={6} />
        ) : parcelsError ? (
          <EmptyState
            compact
            icon='ri-error-warning-line'
            title='Delivery Unit gagal dimuat'
            emptyHint='Pilihan Negotiation tidak bisa disimpan sebelum data Delivery Unit tersedia.'
            action={
              onRetryParcels
                ? {
                    label: 'Muat ulang',
                    icon: 'ri-refresh-line',
                    onClick: onRetryParcels,
                  }
                : null
            }
          />
        ) : !parcelItems.length ? (
          <EmptyState
            compact
            title='Belum ada Delivery Unit'
            emptyHint='Pengadaan ini belum punya Delivery Unit untuk dibawa ke Negotiation.'
          />
        ) : (
          <NegotiationWizard
            parcels={parcelItems}
            procurement={procurement}
            selectionByParcel={selectionByParcel}
            blockedRequirements={blockedRequirements}
            onSelectRequirement={handleSelectRequirement}
            renderOfferTable={renderOfferTable}
          />
        )}

        <div className='req-actionbar flex-wrap align-items-center justify-content-between gap-3'>
          <div className='flex-grow-1 me-2' style={{ minWidth: 0 }}>
            {allParcelsLocked ? (
              <span className='text-muted small align-self-center'>
                Seluruh Delivery Unit telah memasuki tahap Negotiation. Tidak
                ada perubahan pilihan yang perlu disimpan.
              </span>
            ) : actionHint ? (
              <div
                className='alert alert-warning py-2 px-3 mb-0 small text-break'
                role='status'
              >
                {actionHint}
              </div>
            ) : selectedOffers.length ? (
              <span className='fw-semibold text-dark align-self-center'>
                {selectedOffers.length} Offer dipilih untuk disimpan
              </span>
            ) : null}
          </div>
          {!allParcelsLocked ? (
            <div className='d-inline-flex align-items-center gap-2 flex-wrap flex-shrink-0'>
              <ActionButton
                variant='primary'
                icon='ri-save-line'
                loading={isSaving}
                disabled={
                  !isDataReady || Boolean(parcelsError) || !parcelItems.length
                }
                onClick={handleSaveDraft}
              >
                Simpan Pilihan
                {selectedOffers.length ? ` (${selectedOffers.length})` : ''}
              </ActionButton>
            </div>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
};

export default Negotiation;
